import { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'
import { adminOrderService } from '../services'
import { getApiErrorMessage } from '../services/apiClient'
import { formatDateTime } from '../utils/dateFormat'
import TableEmptyState from '../components/shared/TableEmptyState'
import TableLoadingRow from '../components/shared/TableLoadingRow'
import PaymentProofEditAfterRejectDialog from '../components/orders/PaymentProofEditAfterRejectDialog'

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100]

const headerCellSx = { fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap' }

function formatAmount(value) {
  const n = Number(value ?? 0)
  return `৳${n.toFixed(2)}`
}

export default function RejectedPaymentProofsPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [editTarget, setEditTarget] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await adminOrderService.listRejectedPaymentProofRequests({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      })
      setItems(res?.items ?? [])
      setTotal(res?.total ?? res?.items?.length ?? 0)
    } catch (err) {
      setItems([])
      setTotal(0)
      setError(getApiErrorMessage(err, 'Failed to load rejected payment proofs'))
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage])

  useEffect(() => {
    load()
  }, [load])

  const handleEditSaved = () => {
    setEditTarget(null)
    setSuccessMessage('Payment proof updated and resubmitted for verification.')
    load()
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/orders/purchase')}
          color="inherit"
          sx={{ textTransform: 'none' }}
        >
          Purchase orders
        </Button>
        <Tooltip title="Refresh list">
          <span>
            <IconButton onClick={load} disabled={loading} aria-label="Refresh">
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Rejected payment proofs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All payment proof requests that finance rejected. Edit details and resubmit from here;
          changes are logged.
        </Typography>
      </Stack>

      {successMessage ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                <TableCell sx={headerCellSx}>Request</TableCell>
                <TableCell sx={headerCellSx}>Order</TableCell>
                <TableCell sx={headerCellSx}>Shop</TableCell>
                <TableCell sx={headerCellSx} align="right">
                  Amount
                </TableCell>
                <TableCell sx={headerCellSx}>Rejected</TableCell>
                <TableCell sx={headerCellSx}>Rejection reason</TableCell>
                <TableCell sx={headerCellSx} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableLoadingRow colSpan={7} />
              ) : items.length === 0 ? (
                <TableEmptyState colSpan={7} message="No rejected payment proof requests." />
              ) : (
                items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>#{row.id}</TableCell>
                    <TableCell>
                      <Link component={RouterLink} to={`/orders/${row.order_id}`}>
                        #{row.order_id}
                      </Link>
                    </TableCell>
                    <TableCell>{row.organization_name || '—'}</TableCell>
                    <TableCell align="right">{formatAmount(row.requested_amount)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(row.updated_at || row.created_at)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>
                      {row.rejection_reason || '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() =>
                          setEditTarget({ orderId: row.order_id, requestId: row.id })
                        }
                        sx={{ bgcolor: '#11809A', '&:hover': { bgcolor: '#0f6d82' } }}
                      >
                        Edit & resubmit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      </Card>

      {editTarget ? (
        <PaymentProofEditAfterRejectDialog
          open
          orderId={editTarget.orderId}
          requestId={editTarget.requestId}
          onClose={() => setEditTarget(null)}
          onSaved={handleEditSaved}
        />
      ) : null}
    </Box>
  )
}
